import { useCallback, useEffect, useRef, useState } from "react";
import { Plus, Money, Paperclip, Trash, FileArrowUp } from "@phosphor-icons/react";
import { toast } from "sonner";
import { ModalShell } from "../components/ModalShell";
import { useAuth } from "../context/AuthContext";
import api, { fmtGNF, fmtDate, apiError, logError } from "../lib/api";
import { AmountInput } from "../components/AmountInput";

const currentPeriod = () => new Date().toISOString().slice(0, 7);
const EMPTY_PAY = { period: currentPeriod(), base_salary: "", bonus: "", overtime: "", note: "", duplicate_reason: "" };

function DocumentsPanel({ emp }) {
  const [docs, setDocs] = useState([]);
  const [label, setLabel] = useState("");
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);

  const load = useCallback(() => api.get(`/employees/${emp.id}/documents`)
    .then((r) => setDocs(r.data)).catch((e) => logError(e)), [emp.id]);
  useEffect(() => { load(); }, [load]);

  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    fd.append("label", label);
    setBusy(true);
    try {
      await api.post(`/employees/${emp.id}/documents`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Document ajouté"); setLabel(""); load();
    } catch (err) { toast.error(apiError(err)); } finally {
      setBusy(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const open = async (d) => {
    try {
      const res = await api.get(`/employees/${emp.id}/documents/${d.id}`, { responseType: "blob" });
      window.open(URL.createObjectURL(new Blob([res.data], { type: d.content_type })), "_blank");
    } catch (err) { toast.error(apiError(err)); }
  };

  const remove = async (d) => {
    try {
      await api.delete(`/employees/${emp.id}/documents/${d.id}`);
      toast.success("Document retiré — action tracée"); load();
    } catch (err) { toast.error(apiError(err)); }
  };

  return (
    <div className="mt-3 pt-3 border-t border-[#EFE9DE]" data-testid={`employee-documents-${emp.id}`}>
      <p className="text-xs font-semibold text-gray-600 mb-2 flex items-center gap-1.5"><Paperclip size={13} /> Documents (contrat, pièce d'identité…)</p>
      <div className="space-y-1.5">
        {docs.map((d) => (
          <div key={d.id} data-testid="employee-document-row" className="flex items-center gap-2 px-3 py-2 rounded-xl bg-[#F6F4EF]/60 text-xs">
            <button onClick={() => open(d)} data-testid="employee-document-open" className="flex-1 min-w-0 truncate text-left font-semibold text-[#3B6AA0]">{d.label}</button>
            <span className="text-gray-400">{fmtDate(d.created_at)}</span>
            <button onClick={() => remove(d)} data-testid="employee-document-delete" className="text-[#B4432F]"><Trash size={13} /></button>
          </div>
        ))}
        {docs.length === 0 && <p className="text-xs text-gray-400">Aucun document.</p>}
      </div>
      <div className="flex gap-2 mt-2 flex-wrap">
        <input data-testid="employee-document-label" className="kinput flex-1 min-w-[140px] text-sm" placeholder="Intitulé (ex. contrat)" value={label} onChange={(e) => setLabel(e.target.value)} />
        <label className="btn-ghost py-2 px-3 text-xs font-semibold flex items-center gap-1 cursor-pointer">
          <FileArrowUp size={14} /> {busy ? "Envoi..." : "Ajouter"}
          <input ref={fileRef} data-testid="employee-document-input" type="file" className="hidden"
            accept=".pdf,.jpg,.jpeg,.png,.webp" onChange={upload} disabled={busy} />
        </label>
      </div>
    </div>
  );
}

function PayModal({ emp, onClose, onDone }) {
  const [pay, setPay] = useState({ ...EMPTY_PAY, base_salary: String(emp.monthly_salary || "") });
  const [duplicate, setDuplicate] = useState(null);
  const [busy, setBusy] = useState(false);
  const total = (parseFloat(pay.base_salary) || 0) + (parseFloat(pay.bonus) || 0) + (parseFloat(pay.overtime) || 0);

  const submit = async (confirmDuplicate) => {
    setBusy(true);
    try {
      await api.post(`/employees/${emp.id}/pay`, {
        period: pay.period,
        base_salary: parseFloat(pay.base_salary) || 0,
        bonus: parseFloat(pay.bonus) || 0,
        overtime: parseFloat(pay.overtime) || 0,
        note: pay.note,
        confirm_duplicate: confirmDuplicate,
        duplicate_reason: pay.duplicate_reason,
      });
      toast.success("Paiement enregistré — dépense « Salaires » créée automatiquement");
      onDone();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      if (e?.response?.status === 409 && detail?.code === "salaire_deja_paye") setDuplicate(detail);
      else toast.error(apiError(e));
    } finally { setBusy(false); }
  };

  return (
    <ModalShell size="max-w-sm" onClose={onClose}>
        <h3 className="font-display font-bold text-lg">Payer {emp.name}</h3>
        <div>
          <p className="text-xs text-gray-500 mb-1">Période concernée (obligatoire)</p>
          <input data-testid="employee-pay-period-input" type="month" className="kinput" value={pay.period}
            onChange={(e) => { setPay({ ...pay, period: e.target.value }); setDuplicate(null); }} />
        </div>
        <div className="space-y-2">
          {[["base_salary", "Salaire de base (GNF)"], ["bonus", "Prime (GNF, si applicable)"], ["overtime", "Heures supplémentaires (GNF)"]].map(([k, l]) => (
            <AmountInput key={k} data-testid={`employee-pay-${k}-input`} placeholder={l}
              value={pay[k]} onChange={(v) => setPay({ ...pay, [k]: v })} />
          ))}
        </div>
        <p data-testid="employee-pay-total" className="text-sm font-semibold">Total versé : {fmtGNF(total)}</p>
        <input data-testid="employee-pay-note-input" className="kinput" placeholder="Note (optionnel)" value={pay.note} onChange={(e) => setPay({ ...pay, note: e.target.value })} />

        {duplicate && (
          <div data-testid="employee-pay-duplicate-alert" className="space-y-2 bg-[#F7EFD9] text-[#8a6a14] rounded-xl px-3 py-2.5">
            <p className="text-sm">{duplicate.message}</p>
            <input data-testid="employee-pay-duplicate-reason" className="kinput bg-white" placeholder="Motif obligatoire (ex. prime exceptionnelle)"
              value={pay.duplicate_reason} onChange={(e) => setPay({ ...pay, duplicate_reason: e.target.value })} />
            <button data-testid="employee-pay-duplicate-confirm" className="btn-amber w-full py-2.5"
              disabled={busy || !pay.duplicate_reason.trim()} onClick={() => submit(true)}>
              Confirmer quand même ce paiement
            </button>
          </div>
        )}

        <div className="flex gap-2">
          <button className="btn-ghost flex-1 py-2.5" onClick={onClose}>Annuler</button>
          <button data-testid="employee-pay-confirm-btn" className="btn-amber flex-1 py-2.5"
            disabled={busy || total <= 0 || !pay.period} onClick={() => submit(false)}>Payer</button>
        </div>
    </ModalShell>
  );
}

export default function Employees() {
  const { user } = useAuth();
  const [emps, setEmps] = useState([]);
  const [showAdd, setShowAdd] = useState(false);
  const [form, setForm] = useState({ first_name: "", last_name: "", phone: "", function: "", monthly_hours: "", hire_date: "", monthly_salary: "" });
  const [payEmp, setPayEmp] = useState(null);
  const [openDocs, setOpenDocs] = useState({});

  const load = useCallback(() => api.get("/employees").then((r) => setEmps(r.data)).catch((e) => logError(e)), []);
  useEffect(() => { load(); }, [load]);

  const create = async () => {
    try {
      await api.post("/employees", {
        first_name: form.first_name, last_name: form.last_name, phone: form.phone,
        function: form.function, monthly_hours: parseFloat(form.monthly_hours) || 0,
        hire_date: form.hire_date, monthly_salary: parseFloat(form.monthly_salary) || 0,
      });
      toast.success("Employé ajouté — aucun accès au logiciel n'est créé");
      setShowAdd(false);
      setForm({ first_name: "", last_name: "", phone: "", function: "", monthly_hours: "", hire_date: "", monthly_salary: "" });
      load();
    } catch (e) { toast.error(apiError(e)); }
  };

  return (
    <div className="space-y-4 fade-in" data-testid="employees-page">
      {user?.role !== "patron" && (
        <div data-testid="employees-forbidden" className="kcard p-8 text-center text-sm text-gray-600">
          Section réservée au patron. Les salaires ne sont visibles par aucun autre rôle.
        </div>
      )}
      {user?.role === "patron" && (<>
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-display font-bold text-2xl">Employés & RH</h1>
          <p className="text-xs text-gray-500">Section strictement réservée au patron.</p>
        </div>
        <button data-testid="add-employee-btn" onClick={() => setShowAdd(true)} className="btn-amber py-2 px-4 flex items-center gap-1.5 text-sm"><Plus size={16} weight="bold" /> Employé</button>
      </div>

      <div className="space-y-2">
        {emps.map((e) => (
          <div key={e.id} data-testid="employee-row" className="kcard p-4">
            <div className="flex items-center gap-3 flex-wrap">
              <div className="flex-1 min-w-0">
                <p className="font-semibold text-sm">{e.name}</p>
                <p className="text-xs text-gray-500">{e.function || "—"} · {e.phone || "téléphone non renseigné"} · Salaire fixe : {fmtGNF(e.monthly_salary)}/mois{e.monthly_hours ? ` · ${e.monthly_hours}h/mois` : ""}</p>
              </div>
              <button data-testid="employee-docs-toggle" onClick={() => setOpenDocs((s) => ({ ...s, [e.id]: !s[e.id] }))}
                className="btn-ghost py-1.5 px-3 text-xs flex items-center gap-1"><Paperclip size={14} /> Documents</button>
              <button data-testid="pay-employee-btn" onClick={() => setPayEmp(e)}
                className="btn-ghost py-1.5 px-3 text-xs flex items-center gap-1"><Money size={14} /> Payer</button>
            </div>
            {e.payments?.length > 0 && (
              <div className="mt-2 space-y-1">
                {e.payments.slice(-3).reverse().map((p) => (
                  <p key={p.date} data-testid="employee-payment-row" className="text-xs text-gray-500">
                    {p.period_label || p.period || "période non précisée"} — {fmtGNF(p.amount)} · {fmtDate(p.date)}
                    {p.duplicate && <span className="text-[#C99A2E]"> · doublon confirmé : {p.duplicate_reason}</span>}
                  </p>
                ))}
              </div>
            )}
            {openDocs[e.id] && <DocumentsPanel emp={e} />}
          </div>
        ))}
        {emps.length === 0 && <div className="kcard p-8 text-center text-sm text-gray-400">Aucun employé enregistré.</div>}
      </div>

      {showAdd && (
        <ModalShell size="max-w-sm" onClose={() => setShowAdd(false)}>
            <h3 className="font-display font-bold text-lg">Nouvel employé</h3>
            <p className="text-xs text-gray-500">Prénom, nom et téléphone sont obligatoires. L'employé n'a pas besoin d'un accès au logiciel.</p>
            <div className="grid grid-cols-2 gap-2">
              <input data-testid="employee-firstname-input" className="kinput" placeholder="Prénom" value={form.first_name} onChange={(e) => setForm({ ...form, first_name: e.target.value })} />
              <input data-testid="employee-lastname-input" className="kinput" placeholder="Nom" value={form.last_name} onChange={(e) => setForm({ ...form, last_name: e.target.value })} />
            </div>
            <input data-testid="employee-phone-input" className="kinput" placeholder="Téléphone (obligatoire)" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} />
            <input data-testid="employee-function-input" className="kinput" placeholder="Fonction" value={form.function} onChange={(e) => setForm({ ...form, function: e.target.value })} />
            <div className="grid grid-cols-2 gap-2">
              <input data-testid="employee-hours-input" type="number" className="kinput" placeholder="Heures prévues / mois" value={form.monthly_hours} onChange={(e) => setForm({ ...form, monthly_hours: e.target.value })} />
              <input data-testid="employee-hiredate-input" type="date" className="kinput" value={form.hire_date} onChange={(e) => setForm({ ...form, hire_date: e.target.value })} />
            </div>
            <AmountInput data-testid="employee-salary-input" onOverflow={(m) => toast.error(m)} placeholder="Salaire fixe mensuel (GNF)" value={form.monthly_salary} onChange={(v) => setForm({ ...form, monthly_salary: v })} />
            <div className="flex gap-2">
              <button className="btn-ghost flex-1 py-2.5" onClick={() => setShowAdd(false)}>Annuler</button>
              <button data-testid="employee-create-btn" className="btn-amber flex-1 py-2.5" disabled={!form.first_name || !form.last_name || !form.phone} onClick={create}>Ajouter</button>
            </div>
          </ModalShell>
      )}

      {payEmp && <PayModal emp={payEmp} onClose={() => setPayEmp(null)} onDone={() => { setPayEmp(null); load(); }} />}
      </>)}
    </div>
  );
}
